import * as React from 'react'
import { useCallback, useMemo } from 'react'

import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  CardList,
  CardListSummaryBar,
  ICardListItemPosition,
} from '~/client/src/shared/components/CardList'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import OptionsHeader from '~/client/src/shared/components/OptionsList/pieces/OptionsHeader'
import { PanelSections } from '~/client/src/shared/components/PanelSections/PanelSections'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import ListSelectorContext, {
  useListSelectorContext,
} from './ListSelectorContext'
import {
  IListSelectorGroupHeaderExtras,
  ListSelectorGroupHeader,
  ListSelectorRow,
} from './ListSelectorRow'
import { ListSelectorToolbar } from './ListSelectorToolbar'
import {
  IListSelectorGroupConfig,
  IListSelectorGroupHeaderInfo,
  IListSelectorGroupSelection,
  IListSelectorItem,
  IListSelectorSearchConfig,
  IListSelectorSortConfig,
  SelectionMode,
} from './types'
import { useListSelectorState } from './useListSelectorState'

// ─── Root ────────────────────────────────────────────────────────────────────

interface IRootProps<T extends IListSelectorItem> {
  items: T[]
  selectedIds: Set<string>
  onItemToggle?: (id: string) => void
  onToggleAll?: (ids: string[], selectAll: boolean) => void
  draft?: boolean
  selectionMode?: SelectionMode
  onClose?: () => void
  search?: IListSelectorSearchConfig<T>
  sort?: IListSelectorSortConfig<T>
  grouping?: IListSelectorGroupConfig<T>
  /** Listed and counted but unpickable: the row renders disabled and the select-all and band toggles skip it. */
  isItemDisabled?: (item: T) => boolean
  className?: string
  children: React.ReactNode
}

function ListSelectorRoot<T extends IListSelectorItem>({
  items,
  selectedIds,
  onItemToggle,
  onToggleAll,
  draft,
  selectionMode,
  onClose,
  search,
  sort,
  grouping,
  isItemDisabled,
  className,
  children,
}: IRootProps<T>): React.ReactElement {
  const state = useListSelectorState({
    items,
    selectedIds,
    onItemToggle,
    onToggleAll,
    showActions: draft,
    selectionMode,
    onClose,
    search,
    sort,
    grouping,
    isItemDisabled,
  })

  // Scopes in-box overlays (the .Counter tree-grouping kebab) to this selector.
  const [portalId] = React.useState(() => {
    nextListSelectorPortalId += 1
    return `list-selector-portal-${nextListSelectorPortalId}`
  })
  const portalSelector = `[${PORTAL_ATTRIBUTE}="${portalId}"]`

  const ctxValue = React.useMemo(
    () => ({
      ...state,
      searchConfig: search,
      sortConfig: sort,
      groupingOptions: grouping?.options,
      isItemDisabled,
      portalSelector,
    }),
    [state, search, sort, grouping?.options, isItemDisabled, portalSelector],
  )

  return (
    <ListSelectorContext.Provider value={ctxValue}>
      <div
        className={classList({
          'tw:flex tw:flex-col tw:overflow-hidden tw:relative': true,
          [className || '']: !!className,
        })}
      >
        {children}
        <div {...{ [PORTAL_ATTRIBUTE]: portalId }} />
      </div>
    </ListSelectorContext.Provider>
  )
}

const PORTAL_ATTRIBUTE = 'data-list-selector-portal'
let nextListSelectorPortalId = 0

// ─── Title ───────────────────────────────────────────────────────────────────

export type ListSelectorTitleVariant = 'popover' | 'panel'

interface ITitleProps {
  title: string
  onClose?: () => void
  closeLabel?: string
  /** `panel` when the selector fills a side panel: the panel header, not the popover band. */
  variant?: ListSelectorTitleVariant
}

function ListSelectorTitle({
  title,
  onClose,
  closeLabel,
  variant = 'popover',
}: ITitleProps) {
  if (variant === 'panel') {
    return (
      <div data-slot="list-selector-title" className="tw:shrink-0">
        <PanelSections.Header
          title={title}
          onClose={onClose}
          closeLabel={closeLabel}
        />
      </div>
    )
  }

  return (
    <div
      data-slot="list-selector-title"
      className="tw:px-2.5 tw:py-2.5 tw:bg-background tw:shrink-0"
    >
      <OptionsHeader title={title} onClose={onClose} closeLabel={closeLabel} />
    </div>
  )
}

// ─── Counter ─────────────────────────────────────────────────────────────────

interface ICounterProps {
  /** Total-count label, e.g. `xUsersCount`. Defaults to `xItemsCount`. */
  formatTotal?: (count: number) => string
  children?: (total: number, selected: number) => React.ReactNode
}

function ListSelectorCounter({ formatTotal, children }: ICounterProps) {
  const ctx = useListSelectorContext()
  const isSingle = ctx.selectionMode === 'single'

  return (
    <CardListSummaryBar
      selectAll={
        isSingle
          ? undefined
          : {
              checked: ctx.areAllSelected,
              indeterminate: ctx.isAnySelected && !ctx.areAllSelected,
              disabled: !ctx.hasItems,
              onToggle: ctx.handleToggleAll,
              ariaLabel: Localization.translator.selectAll,
            }
      }
      listState={ctx.groupConfig ? ctx.listState : undefined}
      portalSelector={ctx.portalSelector}
    >
      {children
        ? children(ctx.processedItems.length, ctx.selectedCount)
        : !isSingle && ctx.selectedCount > 0
          ? Localization.translator.xSelected(ctx.selectedCount)
          : (formatTotal ?? Localization.translator.xItemsCount)(
              ctx.processedItems.length,
            )}
    </CardListSummaryBar>
  )
}

// ─── List ────────────────────────────────────────────────────────────────────

interface IListProps<T extends IListSelectorItem> {
  renderItemContent?: (item: T) => React.ReactNode
  renderItem?: (
    item: T,
    isSelected: boolean,
    onToggle: () => void,
    isDisabled: boolean,
  ) => React.ReactNode
  renderGroupContent?: (
    group: IListSelectorGroupHeaderInfo<T>,
  ) => React.ReactNode
  /** Per-band label colour and trailing slot, keeping the default header's own wiring. */
  getGroupHeaderProps?: (
    group: IListSelectorGroupHeaderInfo<T>,
  ) => IListSelectorGroupHeaderExtras | undefined
  renderGroupHeader?: (
    group: IListSelectorGroupHeaderInfo<T>,
    onToggle: () => void,
    selection: IListSelectorGroupSelection,
    onGroupCheckbox: (e: React.MouseEvent) => void,
  ) => React.ReactNode
  /** For a `max-h-*` host with no pinned height; see CardList's prop. */
  parentSizesToContent?: boolean
  children?: React.ReactNode
}

function ListSelectorList<T extends IListSelectorItem>({
  renderItemContent,
  renderItem,
  renderGroupContent,
  getGroupHeaderProps,
  renderGroupHeader,
  parentSizesToContent,
  children,
}: IListProps<T>) {
  const ctx = useListSelectorContext<T>()

  const renderCardListItem = useCallback(
    (item: T, _depth?: number, position?: ICardListItemPosition) => {
      const isSelected = ctx.effectiveIds.has(item.id)
      const isDisabled = !!ctx.isItemDisabled?.(item)
      const onToggle = () => ctx.effectiveToggle?.(item.id)

      return (
        <div
          className={classList({
            'tw:px-2.5 tw:py-px': true,
            'tw:pt-2.5': !!position?.isFirst,
            'tw:pb-2.5': !!position?.isLast,
          })}
        >
          {renderItem ? (
            renderItem(item, isSelected, onToggle, isDisabled)
          ) : (
            <ListSelectorRow
              isSelected={isSelected}
              onToggle={onToggle}
              disabled={isDisabled}
            >
              {renderItemContent?.(item)}
            </ListSelectorRow>
          )}
        </div>
      )
    },
    [
      ctx.effectiveIds,
      ctx.effectiveToggle,
      ctx.isItemDisabled,
      renderItem,
      renderItemContent,
    ],
  )

  // Tree mode buckets items into leaf bands only, so an ancestor band's own
  // `items` is empty (useCardListCore). Roll each leaf's ids up to its ancestors
  // so an ancestor's checkbox governs its whole subtree; a Set de-dupes the item
  // that `groupBy` put in two leaves under one ancestor.
  const idsByGroup = useMemo(() => {
    const byGroup = new Map<string, Set<string>>()
    const getAncestors = ctx.groupConfig?.getGroupAncestors
    const add = (key: string, ids: string[]) => {
      const bucket = byGroup.get(key)
      if (bucket) ids.forEach(id => bucket.add(id))
      else byGroup.set(key, new Set(ids))
    }
    ctx.listState.groups.forEach(group => {
      const ids = group.items
        .filter(item => !ctx.isItemDisabled?.(item))
        .map(item => item.id)
      add(group.key, ids)
      getAncestors?.(group.key).forEach(ancestor => add(ancestor, ids))
    })
    // Flatten once here, not per header render — an ancestor band's bucket is
    // effectively the whole list, and headers re-render on every keystroke.
    return new Map(Array.from(byGroup, ([key, ids]) => [key, Array.from(ids)]))
  }, [ctx.listState.groups, ctx.groupConfig, ctx.isItemDisabled])

  const headersWithTopBorder = useMemo(() => {
    const groups = ctx.listState.visibleGroups ?? ctx.listState.groups
    const keys = new Set<string>()
    groups.forEach((group, index) => {
      const previous = groups[index - 1]
      if (previous && !previous.isCollapsed && previous.items.length > 0) {
        keys.add(group.key)
      }
    })
    return keys
  }, [ctx.listState.visibleGroups, ctx.listState.groups])

  const renderCardListGroupHeader = useCallback(
    (group: IListSelectorGroupHeaderInfo<T>, onToggle: () => void) => {
      const allIds = idsByGroup.get(group.key) ?? []
      const allSelected =
        allIds.length > 0 && allIds.every(id => ctx.effectiveIds.has(id))
      const someSelected =
        !allSelected && allIds.some(id => ctx.effectiveIds.has(id))
      const selection = {
        allSelected,
        someSelected,
        hasSelectable: allIds.length > 0,
      }

      const handleGroupCheckbox = (e?: React.MouseEvent) => {
        e?.stopPropagation()
        if (ctx.effectiveToggleAll) {
          ctx.effectiveToggleAll(allIds, !allSelected)
        } else if (ctx.effectiveToggle) {
          allIds.forEach(id => ctx.effectiveToggle?.(id))
        }
      }

      if (renderGroupHeader) {
        return renderGroupHeader(
          group,
          onToggle,
          selection,
          handleGroupCheckbox,
        )
      }

      return (
        <ListSelectorGroupHeader
          group={group}
          onToggle={onToggle}
          selection={selection}
          onGroupCheckbox={handleGroupCheckbox}
          hasTopBorder={headersWithTopBorder.has(group.key)}
          {...getGroupHeaderProps?.(group)}
        >
          {renderGroupContent?.(group)}
        </ListSelectorGroupHeader>
      )
    },
    [
      renderGroupHeader,
      renderGroupContent,
      getGroupHeaderProps,
      idsByGroup,
      headersWithTopBorder,
      ctx.effectiveIds,
      ctx.effectiveToggleAll,
      ctx.effectiveToggle,
    ],
  )

  return (
    <CardList
      items={ctx.processedItems}
      listState={ctx.groupConfig ? ctx.listState : undefined}
      renderItem={renderCardListItem}
      renderGroupHeader={
        ctx.groupConfig ? renderCardListGroupHeader : undefined
      }
      empty={
        children ?? (
          <div className="tw:text-center tw:py-6 tw:text-body-md tw:text-muted-foreground">
            {Localization.translator.noResults}
          </div>
        )
      }
      className="tw:flex-1"
      role="listbox"
      ariaMultiselectable={ctx.selectionMode !== 'single'}
      estimatedItemHeight={30}
      parentSizesToContent={parentSizesToContent}
    />
  )
}

// ─── Actions ─────────────────────────────────────────────────────────────────

interface IActionsProps {
  onApply?: (selectedIds: Set<string>) => void
  onClose?: () => void
  onCreateNew?: () => void
  disableApplyWhenEmpty?: boolean
  applyLabel?: string
  /** Disables Apply outright, whatever the draft holds. */
  isApplyDisabled?: boolean
}

function ListSelectorActions({
  onApply,
  onClose,
  onCreateNew,
  disableApplyWhenEmpty,
  applyLabel,
  isApplyDisabled: isApplyDisabledProp,
}: IActionsProps) {
  const ctx = useListSelectorContext()
  const isApplyDisabled =
    !!isApplyDisabledProp ||
    (!!disableApplyWhenEmpty && ctx.draftIds.size === 0)

  return (
    <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:p-3 tw:border-0 tw:border-t tw:border-solid tw:border-input-soft tw:shrink-0">
      {onCreateNew ? (
        <Button variant="outline" size="lg" onClick={onCreateNew}>
          <AddIcon data-icon="inline-start" size="xs" />
          {Localization.translator.createNew}
        </Button>
      ) : (
        <div />
      )}
      <div className="tw:flex tw:items-center tw:gap-2">
        <Button variant="outline" size="lg" onClick={onClose}>
          {Localization.translator.cancel}
        </Button>
        <Button
          variant="default"
          size="lg"
          onClick={() => onApply?.(ctx.draftIds)}
          disabled={isApplyDisabled}
        >
          {applyLabel ?? Localization.translator.apply}
        </Button>
      </div>
    </div>
  )
}

// ─── Namespace export ────────────────────────────────────────────────────────

export const ListSelector = {
  Root: ListSelectorRoot,
  Title: ListSelectorTitle,
  Toolbar: ListSelectorToolbar,
  Counter: ListSelectorCounter,
  List: ListSelectorList,
  Actions: ListSelectorActions,
  Row: ListSelectorRow,
  GroupHeader: ListSelectorGroupHeader,
}
